import type { NoteFormat } from "@ccnote/shared";
import { useDeferredValue, useMemo, useRef, useState } from "react";
import { renderMarkdown } from "../markdown";

export interface Draft {
  title: string;
  body: string;
  format: NoteFormat;
}

const FORMAT_KEY = "ccnote.editorFormat";

/** The format new notes start in: whatever this person used last on this device. */
export function preferredFormat(): NoteFormat {
  try {
    return localStorage.getItem(FORMAT_KEY) === "markdown" ? "markdown" : "text";
  } catch {
    return "text";
  }
}

function rememberFormat(format: NoteFormat) {
  try {
    localStorage.setItem(FORMAT_KEY, format);
  } catch {
    // Private mode or storage disabled: just don't remember.
  }
}

/**
 * Title + body editor used for new notes and for editing. Plain text is a single textarea;
 * Markdown adds a toolbar and a live preview beside the editor (Write/Preview tabs on phones).
 */
export function NoteEditor({
  initial,
  saving,
  error,
  headerExtra,
  saveLabel = "Save",
  onSave,
  onCancel,
}: {
  initial: Draft;
  saving: boolean;
  error?: string | null;
  headerExtra?: React.ReactNode;
  saveLabel?: string;
  onSave: (draft: Draft) => void;
  onCancel: () => void;
}) {
  const [draft, setDraft] = useState(initial);
  const [tab, setTab] = useState<"write" | "preview">("write");
  const bodyRef = useRef<HTMLTextAreaElement>(null);
  const isMarkdown = draft.format === "markdown";
  const empty = !draft.title.trim() && !draft.body.trim();

  const deferredBody = useDeferredValue(draft.body);
  const previewHtml = useMemo(() => (isMarkdown ? renderMarkdown(deferredBody) : ""), [isMarkdown, deferredBody]);

  const setFormat = (format: NoteFormat) => {
    setDraft((d) => ({ ...d, format }));
    rememberFormat(format);
    setTab("write");
  };

  const save = () => {
    if (!empty && !saving) onSave(draft);
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLElement>) => {
    const mod = e.metaKey || e.ctrlKey;
    if (mod && e.key.toLowerCase() === "s") {
      e.preventDefault();
      save();
    }
  };

  const onBodyKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (!isMarkdown) return;
    const ta = e.currentTarget;
    const mod = e.metaKey || e.ctrlKey;
    const key = e.key.toLowerCase();
    if (mod && key === "b") return e.preventDefault(), wrap(ta, "**", "**", "bold text");
    if (mod && key === "i") return e.preventDefault(), wrap(ta, "_", "_", "italic text");
    if (mod && key === "k") return e.preventDefault(), insertLink(ta);
    if (e.key === "Enter" && !e.shiftKey && !mod && !e.nativeEvent.isComposing && continueList(ta)) e.preventDefault();
  };

  return (
    <form
      className="editor"
      onKeyDown={onKeyDown}
      onSubmit={(e) => {
        e.preventDefault();
        save();
      }}
    >
      <header className="detail-header">
        <button type="button" className="btn btn-small" onClick={onCancel}>
          Cancel
        </button>
        {headerExtra}
        <div className="segmented" role="radiogroup" aria-label="Format">
          {(["text", "markdown"] as const).map((f) => (
            <button
              key={f}
              type="button"
              role="radio"
              aria-checked={draft.format === f}
              className={draft.format === f ? "on" : ""}
              onClick={() => setFormat(f)}
            >
              {f === "text" ? "Text" : "Markdown"}
            </button>
          ))}
        </div>
        <button className="btn btn-primary btn-small save-btn" disabled={empty || saving} title="Save (⌘S)">
          {saving ? "Saving…" : saveLabel}
        </button>
      </header>

      <input
        className="composer-title"
        placeholder="Title"
        value={draft.title}
        onChange={(e) => setDraft({ ...draft, title: e.target.value })}
        autoFocus={!initial.title}
        aria-label="Title"
      />

      {isMarkdown && (
        <div className="md-bar">
          <Toolbar target={bodyRef} />
          <div className="md-tabs" role="tablist" aria-label="Markdown view">
            {(["write", "preview"] as const).map((t) => (
              <button key={t} type="button" role="tab" aria-selected={tab === t} className={tab === t ? "on" : ""} onClick={() => setTab(t)}>
                {t === "write" ? "Write" : "Preview"}
              </button>
            ))}
          </div>
        </div>
      )}

      <div className={isMarkdown ? `md-split show-${tab}` : "text-body"}>
        <textarea
          ref={bodyRef}
          className="composer-body"
          placeholder={isMarkdown ? "Write in Markdown: # heading, **bold**, - list, - [ ] task…" : "Write something…"}
          value={draft.body}
          onChange={(e) => setDraft({ ...draft, body: e.target.value })}
          onKeyDown={onBodyKeyDown}
          autoFocus={Boolean(initial.title)}
          aria-label="Note"
          spellCheck
        />
        {isMarkdown && (
          <div className="md-preview" aria-label="Preview" aria-live="polite">
            {draft.body.trim() ? (
              <div className="markdown-body" dangerouslySetInnerHTML={{ __html: previewHtml }} />
            ) : (
              <p className="muted">Nothing to preview yet.</p>
            )}
          </div>
        )}
      </div>

      {error && <p className="alert editor-alert">{error}</p>}
    </form>
  );
}

function Toolbar({ target }: { target: React.RefObject<HTMLTextAreaElement | null> }) {
  const act = (fn: (ta: HTMLTextAreaElement) => void) => () => target.current && fn(target.current);
  const tools: Array<[string, string, () => void, React.ReactNode]> = [
    ["Heading", "", act((ta) => toggleLinePrefix(ta, "## ")), <b key="h">H</b>],
    ["Bold", "⌘B", act((ta) => wrap(ta, "**", "**", "bold text")), <b key="b">B</b>],
    ["Italic", "⌘I", act((ta) => wrap(ta, "_", "_", "italic text")), <i key="i">I</i>],
    ["Strikethrough", "", act((ta) => wrap(ta, "~~", "~~", "text")), <s key="s">S</s>],
    ["Link", "⌘K", act(insertLink), "🔗"],
    ["Bulleted list", "", act((ta) => toggleLinePrefix(ta, "- ")), "•"],
    ["Numbered list", "", act((ta) => toggleLinePrefix(ta, "1. ")), "1."],
    ["Checklist", "", act((ta) => toggleLinePrefix(ta, "- [ ] ")), "☐"],
    ["Quote", "", act((ta) => toggleLinePrefix(ta, "> ")), "❝"],
    ["Code", "", act(insertCode), <code key="c">{"</>"}</code>],
  ];
  return (
    <div className="md-toolbar" role="toolbar" aria-label="Formatting">
      {tools.map(([label, shortcut, onClick, icon]) => (
        <button
          key={label}
          type="button"
          className="tool"
          title={shortcut ? `${label} (${shortcut})` : label}
          aria-label={label}
          // Keep the textarea's selection while clicking the toolbar.
          onMouseDown={(e) => e.preventDefault()}
          onClick={onClick}
        >
          {icon}
        </button>
      ))}
    </div>
  );
}

/* ── Textarea editing helpers ──────────────────────────────────────────────
 * They go through execCommand("insertText") so each change lands on the browser's
 * undo stack and fires a normal input event (which updates React state).
 */

function replaceRange(ta: HTMLTextAreaElement, start: number, end: number, text: string, selStart: number, selEnd: number) {
  ta.focus();
  ta.setSelectionRange(start, end);
  const ok = document.execCommand?.("insertText", false, text);
  if (!ok) {
    ta.setRangeText(text, start, end, "end");
    ta.dispatchEvent(new Event("input", { bubbles: true }));
  }
  ta.setSelectionRange(selStart, selEnd);
}

function wrap(ta: HTMLTextAreaElement, before: string, after: string, placeholder: string) {
  const { selectionStart: s, selectionEnd: e, value } = ta;
  const selected = value.slice(s, e);
  // Toggle off when the selection is already wrapped.
  if (value.slice(s - before.length, s) === before && value.slice(e, e + after.length) === after) {
    replaceRange(ta, s - before.length, e + after.length, selected, s - before.length, e - before.length);
    return;
  }
  const inner = selected || placeholder;
  replaceRange(ta, s, e, `${before}${inner}${after}`, s + before.length, s + before.length + inner.length);
}

function insertLink(ta: HTMLTextAreaElement) {
  const { selectionStart: s, selectionEnd: e, value } = ta;
  const selected = value.slice(s, e);
  const isUrl = /^https?:\/\/\S+$/.test(selected);
  const text = isUrl ? "link" : selected || "link text";
  const url = isUrl ? selected : "https://";
  const inserted = `[${text}](${url})`;
  // Select the part the person most likely wants to type next.
  const focusStart = isUrl ? s + 1 : s + text.length + 3;
  const focusEnd = isUrl ? s + 1 + text.length : focusStart + url.length;
  replaceRange(ta, s, e, inserted, focusStart, focusEnd);
}

function insertCode(ta: HTMLTextAreaElement) {
  const { selectionStart: s, selectionEnd: e, value } = ta;
  const selected = value.slice(s, e);
  if (selected.includes("\n")) {
    const block = `\`\`\`\n${selected}\n\`\`\``;
    replaceRange(ta, s, e, block, s + 4, s + 4 + selected.length);
  } else {
    wrap(ta, "`", "`", "code");
  }
}

/** Add or remove a prefix ("- ", "## ", "1. ", …) on every line the selection touches. */
function toggleLinePrefix(ta: HTMLTextAreaElement, prefix: string) {
  const { selectionStart: s, selectionEnd: e, value } = ta;
  const lineStart = value.lastIndexOf("\n", s - 1) + 1;
  const nextBreak = value.indexOf("\n", e > s && value[e - 1] === "\n" ? e - 1 : e);
  const lineEnd = nextBreak === -1 ? value.length : nextBreak;
  const lines = value.slice(lineStart, lineEnd).split("\n");

  const numbered = prefix === "1. ";
  const pattern = numbered ? /^\d+\.\s/ : new RegExp(`^${prefix.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}`);
  const allHave = lines.every((l) => pattern.test(l));
  // Strip any other list/heading marker first so e.g. a bullet can become a checklist item.
  const other = /^(#{1,6}\s|>\s|[-*+]\s\[[ xX]\]\s|[-*+]\s|\d+\.\s)/;
  const next = lines.map((l, i) => {
    if (allHave) return l.replace(pattern, "");
    const bare = l.replace(other, "");
    return `${numbered ? `${i + 1}. ` : prefix}${bare}`;
  });
  const text = next.join("\n");
  replaceRange(ta, lineStart, lineEnd, text, lineStart, lineStart + text.length);
}

/**
 * On Enter inside a list item, start the next item with the same marker
 * ("- ", "- [ ] ", "3. " → "4. "). Enter on an empty item ends the list.
 */
function continueList(ta: HTMLTextAreaElement): boolean {
  const { selectionStart: s, selectionEnd: e, value } = ta;
  if (s !== e) return false;
  const lineStart = value.lastIndexOf("\n", s - 1) + 1;
  const line = value.slice(lineStart, s);
  const m = line.match(/^(\s*)([-*+]\s\[[ xX]\]\s|[-*+]\s|(\d+)\.\s|>\s)(.*)$/);
  if (!m) return false;
  const [, indent = "", marker = "", num, rest = ""] = m;
  if (!rest.trim()) {
    // Empty item: remove the marker instead of adding another.
    replaceRange(ta, lineStart, s, "", lineStart, lineStart);
    return true;
  }
  const nextMarker = num ? `${Number(num) + 1}. ` : marker.replace(/\[[xX]\]/, "[ ]");
  const insert = `\n${indent}${nextMarker}`;
  replaceRange(ta, s, e, insert, s + insert.length, s + insert.length);
  return true;
}
